import React, { useState, useEffect } from "react";
import FlagIcon from "../components/FlagIcon";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Alert,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Stack,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, EmojiEvents as TrophyIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { NationsLeagueEvent } from "../types";
import { COUNTRIES, CountryOption } from "../data/countries";

const getFlagEmoji = (isoCode: string): React.ReactNode => <FlagIcon flag={isoCode} />;

const MIN_TEAMS = 8;
const MAX_TEAMS = 12;

const NationsLeagueList: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<NationsLeagueEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  // Each team is one or more countries playing together (e.g. Brazil + Mexico)
  const emptyTeams = (): CountryOption[][] => Array.from({ length: MIN_TEAMS }, () => []);
  const [teamRows, setTeamRows] = useState<CountryOption[][]>(emptyTeams);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("/api/nations-league/");
      const data = await response.json();
      setEvents(Array.isArray(data) ? data : []);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load Nations League editions");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const resetForm = () => {
    setName("");
    setTeamRows(emptyTeams());
  };

  const selectedNames = new Set(teamRows.flat().map((c) => c.name.toLowerCase()));

  const setTeamCountries = (idx: number, countries: CountryOption[]) =>
    setTeamRows((prev) => prev.map((row, i) => (i === idx ? countries : row)));

  const handleCreate = async () => {
    if (!name.trim()) {
      showSnackbar("Please enter a name for this edition");
      return;
    }
    if (teamRows.length !== 8 && teamRows.length !== 12) {
      showSnackbar("An edition needs exactly 8 or 12 teams");
      return;
    }
    if (teamRows.some((row) => row.length === 0)) {
      showSnackbar("Every team needs at least one country");
      return;
    }

    const teams = teamRows.map((row) => ({
      countries: row.map((c) => ({ name: c.name, isoCode: c.isoCode })),
    }));

    try {
      setSaving(true);
      await apiRequest("/api/nations-league/", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), teams }),
      });
      showSnackbar("Nations League edition created!");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to create edition");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrophyIcon fontSize="large" /> Nations League
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Edition
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Alert severity="info">
          No Nations League editions yet. Click "Add Edition" to create the first one.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {events.map((event) => (
            <Card key={event.id} variant="outlined">
              <CardActionArea onClick={() => navigate(`/admin/nations-league/${event.id}`)}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6">{event.name}</Typography>
                    <Chip
                      label={event.status === "active" ? "Active" : "Completed"}
                      color={event.status === "active" ? "success" : "default"}
                      size="small"
                    />
                  </Stack>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, fontSize: "1.5rem" }}>
                    {(event.countries || []).map((c, idx) => (
                      <span key={idx} title={c.name}>
                        {getFlagEmoji(c.isoCode)}
                      </span>
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Nations League Edition</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="e.g. SPCup of Nations 26/27"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 3 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            Teams ({teamRows.length}) — groups are drawn afterwards
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Pick several countries in one row if they play together as a single team. Use 8 teams (2 groups) or 12 teams (3 groups).
          </Typography>

          {teamRows.map((row, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Autocomplete
                multiple
                fullWidth
                options={COUNTRIES.filter(
                  (c) => !selectedNames.has(c.name.toLowerCase()) || row.includes(c),
                )}
                getOptionLabel={(c) => c.name}
                value={row}
                onChange={(_, selected) => setTeamCountries(idx, selected)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Team ${idx + 1}`}
                    placeholder="Select country (or several)"
                    size="small"
                  />
                )}
              />
              {teamRows.length > MIN_TEAMS && (
                <IconButton
                  aria-label="Remove team"
                  onClick={() => setTeamRows((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          {teamRows.length < MAX_TEAMS && (
            <Button startIcon={<AddIcon />} onClick={() => setTeamRows((prev) => [...prev, []])}>
              Add team
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>
            {saving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default NationsLeagueList;
